import React, { useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Zap } from 'lucide-react';

interface PlaybackControlsProps {
  isPlaying: boolean;
  currentStepIndex: number;
  totalSteps: number;
  speed: number;
  onPlay: () => void;
  onPause: () => void;
  onStepForward: () => void;
  onStepBackward: () => void;
  onSpeedChange: (speed: number) => void;
  onSeek?: (index: number) => void;
  canStepForward: boolean;
  canStepBackward: boolean;
  algorithmName?: string;
  /** Called when the user presses Escape — lets the parent close panels */
  onEscape?: () => void;
}

const SPEED_PRESETS = [0.5, 1, 2, 4] as const;

const PlaybackControls: React.FC<PlaybackControlsProps> = memo(({
  isPlaying,
  currentStepIndex,
  totalSteps,
  speed,
  onPlay,
  onPause,
  onStepForward,
  onStepBackward,
  onSpeedChange,
  onSeek,
  canStepForward,
  canStepBackward,
  algorithmName,
  onEscape,
}) => {
  const progress = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIndexRef = useRef<number>(currentStepIndex);

  const debouncedSeek = useMemo(() => {
    return (index: number) => {
      pendingIndexRef.current = index;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onSeek?.(pendingIndexRef.current);
      }, 80);
    };
  }, [onSeek]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const actionsRef = useRef({ isPlaying, onPlay, onPause, onStepForward, onStepBackward, onEscape });
  actionsRef.current = { isPlaying, onPlay, onPause, onStepForward, onStepBackward, onEscape };

  const isFormTarget = useCallback((target: EventTarget | null): boolean => {
    if (target == null || !(target instanceof Element)) return false;
    const tag = target.tagName.toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON') return true;
    if ((target as HTMLElement).isContentEditable) return true;
    if (target.closest('[role="dialog"]') !== null) return true;
    if (target.closest('[role="listbox"]') !== null) return true;
    if (target.closest('[data-keyboard-trap]') !== null) return true;
    return false;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (isFormTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const { isPlaying: playing, onPlay, onPause, onStepForward, onStepBackward, onEscape } = actionsRef.current;
      switch (e.key) {
        case ' ':          e.preventDefault(); playing ? onPause() : onPlay(); break;
        case 'ArrowRight': e.preventDefault(); onStepForward(); break;
        case 'ArrowLeft':  e.preventDefault(); onStepBackward(); break;
        case 'Home':       e.preventDefault(); onSeek?.(0); break;
        case 'End':        e.preventDefault(); onSeek?.(Math.max(0, totalSteps - 1)); break;
        case 'Escape':     onEscape?.(); break;
      }
    }, { signal: controller.signal });
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormTarget]);

  return (
    <div
      className="flex-shrink-0"
      style={{
        background: 'linear-gradient(180deg, rgba(14,14,16,0.98) 0%, rgba(9,9,11,0.99) 100%)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.35)',
      }}
    >
      {/* ── Seek Scrubber ── */}
      <div className="px-4 pt-3 pb-0">
        <input
          type="range"
          min={0}
          max={Math.max(0, totalSteps - 1)}
          value={currentStepIndex}
          aria-label={`Seek step ${currentStepIndex + 1} of ${totalSteps}`}
          aria-valuenow={currentStepIndex}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, totalSteps - 1)}
          onChange={e => debouncedSeek(Number(e.target.value))}
          disabled={totalSteps === 0}
          className="w-full disabled:opacity-20 disabled:cursor-not-allowed"
          style={{
            background: `linear-gradient(to right, #3b82f6 ${progress}%, rgba(255,255,255,0.08) ${progress}%)`,
          }}
        />
        <div className="flex justify-between mt-1 px-0.5">
          <span className="text-[10px] font-mono tabular-nums" style={{ color: '#52525b' }}>
            {totalSteps > 0 ? currentStepIndex + 1 : 0}
          </span>
          <span className="text-[10px] font-mono tabular-nums" style={{ color: '#52525b' }}>
            {totalSteps}
          </span>
        </div>
      </div>

      {/* ── Controls Row ── */}
      <div className="flex items-center px-4 pb-2.5 pt-1 gap-3">

        {/* Left — algorithm label */}
        <div className="flex-1 min-w-0">
          {algorithmName ? (
            <div
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
              style={{
                background: 'rgba(59,130,246,0.08)',
                border: '1px solid rgba(59,130,246,0.16)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{
                  background: isPlaying ? '#10b981' : '#3b82f6',
                  boxShadow: isPlaying ? '0 0 6px rgba(16,185,129,0.5)' : '0 0 6px rgba(59,130,246,0.4)',
                }}
              />
              <span className="text-[11px] font-medium tracking-tight text-zinc-300 truncate max-w-[130px]">
                {algorithmName}
              </span>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <span className="text-[10px] text-zinc-700 font-mono">No algorithm selected</span>
            </div>
          )}
        </div>

        {/* Center — transport */}
        <div className="flex items-center gap-2">
          <button
            onClick={onStepBackward}
            disabled={!canStepBackward}
            title="Previous (←)"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={e => {
              if (canStepBackward)
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.09)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)';
            }}
          >
            <SkipBack className="w-3.5 h-3.5 text-zinc-300" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={isPlaying ? onPause : onPlay}
            title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 active:scale-95"
            style={{
              background: isPlaying
                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: isPlaying
                ? '0 0 0 1px rgba(99,102,241,0.45), 0 4px 18px rgba(99,102,241,0.32)'
                : '0 0 0 1px rgba(59,130,246,0.45), 0 4px 18px rgba(59,130,246,0.28)',
            }}
          >
            {isPlaying
              ? <Pause className="text-white" style={{ width: 18, height: 18 }} />
              : <Play className="text-white ml-0.5" style={{ width: 18, height: 18 }} />}
          </button>

          <button
            onClick={onStepForward}
            disabled={!canStepForward}
            title="Next (→)"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={e => {
              if (canStepForward)
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.09)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)';
            }}
          >
            <SkipForward className="w-3.5 h-3.5 text-zinc-300" />
          </button>
        </div>

        {/* Right — speed chips */}
        <div className="flex items-center gap-1.5 flex-1 justify-end">
          <Zap className="w-3 h-3 flex-shrink-0" style={{ color: '#3f3f46' }} />
          <div className="flex gap-1">
            {SPEED_PRESETS.map(s => {
              const isActive = speed === s;
              return (
                <button
                  key={s}
                  onClick={() => onSpeedChange(s)}
                  className="px-1.5 py-1 rounded text-[10px] font-mono font-semibold tracking-tight transition-all duration-200"
                  style={{
                    background: isActive ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
                    border: isActive
                      ? '1px solid rgba(59,130,246,0.38)'
                      : '1px solid rgba(255,255,255,0.07)',
                    color:     isActive ? '#60a5fa' : '#52525b',
                    boxShadow: isActive ? '0 0 8px rgba(59,130,246,0.18)' : 'none',
                  }}
                >
                  {s}×
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Keyboard hints ── */}
      <div
        className="playback-hints flex justify-center items-center gap-5 py-1.5"
        style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
      >
        <span className="flex items-center gap-1.5">
          <kbd className="kbd">Space</kbd>
          <span className="text-[10px]" style={{ color: '#3f3f46' }}>play / pause</span>
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="kbd">← →</kbd>
          <span className="text-[10px]" style={{ color: '#3f3f46' }}>step</span>
        </span>
        <span className="flex items-center gap-1.5">
          <kbd className="kbd">Home</kbd>
          <kbd className="kbd">End</kbd>
          <span className="text-[10px]" style={{ color: '#3f3f46' }}>first / last</span>
        </span>
      </div>
    </div>
  );
});

export default PlaybackControls;
